import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import LangToggle from '../components/wiki/LangToggle'
import TagChips from '../components/wiki/TagChips'
import RelatedEntries from '../components/wiki/RelatedEntries'
import VendorCard from '../components/wiki/VendorCard'
import MarkdownView from '../components/wiki/MarkdownView'
import RoleGate from '../auth/RoleGate'
import { useAuth } from '../auth/useAuth'
import { api } from '../auth/api'

const toArray = (val) => {
    if (Array.isArray(val)) return val
    if (typeof val === 'string') {
        try { const p = JSON.parse(val); return Array.isArray(p) ? p : [] }
        catch { return [] }
    }
    return []
}

const toObj = (val, def = {}) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) return val
    if (typeof val === 'string') {
        try { const p = JSON.parse(val); return (p && typeof p === 'object') ? p : def }
        catch { return def }
    }
    return def
}

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '—'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const FILE_IC = {
    pdf: '📕', docx: '📘', doc: '📘', txt: '📄', md: '📄',
    jpg: '🖼️', jpeg: '🖼️', png: '🖼️', csv: '📊', xlsx: '📊',
}

const G7 = new Set(['USA', 'UK', 'Germany', 'France', 'Italy', 'Japan', 'Canada',
                    'United States', 'United Kingdom'])

const LIFECYCLE_COLORS = {
    active:       '#22c55e',
    eol:          '#f59e0b',
    discontinued: '#ef4444',
    replaced:     '#eab308',
    unknown:      '#6b7280',
}

function IntelligenceSection({ entry }) {
    const [open, setOpen] = useState(true)
    const [sourcesOpen, setSourcesOpen] = useState(false)
    const [confidenceOpen, setConfidenceOpen] = useState(false)

    const enriched = !!entry.last_web_enriched_at

    const lcStatus = entry.lifecycle_status
    const lcColor = LIFECYCLE_COLORS[lcStatus] || '#6b7280'

    const ipRating = toObj(entry.ip_rating)
    const leaMission = toObj(entry.lea_mission_types)
    const fromDocs = toArray(leaMission.from_documents)
    const fromTender = toArray(leaMission.from_tender_intel)

    const unstatedUses = toArray(entry.unstated_lea_uses)
    const alternatives = toArray(entry.alternatives)
    const enrichSources = toArray(entry.enrichment_sources)
    const enrichConf = toObj(entry.enrichment_confidence)

    const competitors = alternatives.filter(a => a.relationship === 'competitor')
    const substitutes = alternatives.filter(a => a.relationship === 'substitute')
    const complements = alternatives.filter(a => a.relationship === 'complement')

    const panel = {
        background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
        border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
    }
    const label = { color: '#64748b', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }
    const value = { color: '#e2e8f0', fontSize: '13px', lineHeight: 1.6 }
    const chip = (color) => ({
        display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
        fontSize: '11px', fontWeight: 600, marginRight: '4px', marginBottom: '4px',
        background: `${color}20`, color,
    })

    return (
        <div style={panel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? '16px' : 0 }}>
                <h4 style={{ color: '#d4af37', fontSize: '13px', margin: 0 }}>
                    🧠 Intelligence
                </h4>
                <button onClick={() => setOpen(o => !o)} style={{
                    background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '12px',
                }}>{open ? '▲ Collapse' : '▼ Expand'}</button>
            </div>

            {!open ? null : !enriched ? (
                <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
                    No deep-enrichment data yet. Click <strong style={{ color: '#6366f1' }}>Deep Enrich</strong> to populate.
                </p>
            ) : (
                <div>
                    {/* Lifecycle */}
                    {lcStatus && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={label}>Lifecycle</div>
                            <span style={chip(lcColor)}>{lcStatus.toUpperCase()}</span>
                            {entry.successor_model && (
                                <span style={{ ...value, marginLeft: '8px' }}>
                                    → Replaced by: <strong>{entry.successor_model}</strong>
                                </span>
                            )}
                            {entry.lifecycle_notes && (
                                <p style={{ ...value, color: '#94a3b8', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
                                    {entry.lifecycle_notes}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Procurement */}
                    {(entry.export_controls || entry.sea_distributor || entry.procurement_notes) && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={label}>Procurement</div>
                            {entry.export_controls && (
                                <div style={{ ...value, marginBottom: '4px' }}>
                                    <span style={{ color: '#64748b' }}>Export controls: </span>{entry.export_controls}
                                </div>
                            )}
                            {entry.sea_distributor && (
                                <div style={{ ...value, marginBottom: '4px' }}>
                                    <span style={{ color: '#64748b' }}>SE-Asia distributor: </span>{entry.sea_distributor}
                                </div>
                            )}
                            {entry.procurement_notes && (
                                <div style={{ ...value }}>
                                    <span style={{ color: '#64748b' }}>Notes: </span>{entry.procurement_notes}
                                </div>
                            )}
                        </div>
                    )}

                    {/* IP Rating */}
                    {(ipRating.ingress || ipRating.mil_std) && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={label}>Environmental Rating</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
                                {ipRating.ingress && <span style={chip('#06b6d4')}>{ipRating.ingress}</span>}
                                {ipRating.mil_std && <span style={chip('#8b5cf6')}>{ipRating.mil_std}</span>}
                                {ipRating.operating_temp_c && (
                                    <span style={chip('#64748b')}>
                                        {ipRating.operating_temp_c.min}°C to {ipRating.operating_temp_c.max}°C
                                    </span>
                                )}
                            </div>
                            {ipRating.notes && (
                                <p style={{ ...value, color: '#94a3b8', fontSize: '12px', margin: 0 }}>{ipRating.notes}</p>
                            )}
                        </div>
                    )}

                    {/* LEA Mission Types */}
                    {fromDocs.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={label}>LEA Mission Types</div>
                            <div>
                                {fromDocs.map((m, i) => (
                                    <span key={i} style={chip('#6366f1')}>{m}</span>
                                ))}
                            </div>
                            {fromTender.length > 0 && (
                                <div style={{ marginTop: '6px' }}>
                                    <span style={{ color: '#64748b', fontSize: '11px' }}>From tender intel: </span>
                                    {fromTender.map((m, i) => <span key={i} style={chip('#f59e0b')}>{m}</span>)}
                                </div>
                            )}
                            {leaMission.agreement && leaMission.agreement !== 'n/a' && (
                                <p style={{ ...value, color: '#94a3b8', fontSize: '11px', marginTop: '4px', marginBottom: 0 }}>
                                    agreement: {leaMission.agreement}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Non-standard LEA Uses */}
                    <div style={{ marginBottom: '16px' }}>
                        <div style={label}>🎯 Non-standard operational uses</div>
                        {unstatedUses.length === 0 ? (
                            <p style={{ ...value, color: '#64748b', margin: 0, fontStyle: 'italic' }}>
                                No verified non-standard uses documented.
                            </p>
                        ) : (
                            unstatedUses.map((u, i) => (
                                <div key={i} style={{ ...value, marginBottom: '6px', paddingLeft: '8px', borderLeft: '2px solid #334155' }}>
                                    {u.use}
                                    <span style={{ color: '#64748b', fontSize: '11px', marginLeft: '6px' }}>
                                        [{u.confidence}] · {u.source}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Alternatives */}
                    {alternatives.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={label}>Alternatives</div>
                            {[
                                { title: 'Competitors', items: competitors, color: '#ef4444' },
                                { title: 'Substitutes', items: substitutes, color: '#f59e0b' },
                                { title: 'Complements', items: complements, color: '#22c55e' },
                            ].filter(g => g.items.length > 0).map(g => (
                                <div key={g.title} style={{ marginBottom: '12px' }}>
                                    <div style={{ color: g.color, fontSize: '11px', fontWeight: 700, marginBottom: '6px' }}>
                                        {g.title}
                                    </div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                                                <th style={{ textAlign: 'left', padding: '4px 6px', color: '#64748b', fontWeight: 600 }}>Product</th>
                                                <th style={{ textAlign: 'left', padding: '4px 6px', color: '#64748b', fontWeight: 600 }}>Vendor</th>
                                                <th style={{ textAlign: 'left', padding: '4px 6px', color: '#64748b', fontWeight: 600 }}>Country</th>
                                                <th style={{ textAlign: 'left', padding: '4px 6px', color: '#64748b', fontWeight: 600 }}>vs This</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {g.items.map((a, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                                                    <td style={{ padding: '4px 6px', color: '#e2e8f0' }}>{a.name}</td>
                                                    <td style={{ padding: '4px 6px', color: '#94a3b8' }}>{a.vendor}</td>
                                                    <td style={{ padding: '4px 6px', color: '#94a3b8' }}>
                                                        {a.country}
                                                        {G7.has(a.country) && (
                                                            <span style={chip('#d4af37')} title="G7 country">G7</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '4px 6px', color: '#94a3b8', fontSize: '11px' }}>{a.differentiator}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Known Issues */}
                    {entry.known_issues && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={label}>Known Issues</div>
                            <p style={{ ...value, margin: 0 }}>{entry.known_issues}</p>
                        </div>
                    )}

                    {/* Provenance footer */}
                    <div style={{ borderTop: '1px solid rgba(148,163,184,0.1)', paddingTop: '12px', marginTop: '8px' }}>
                        <p style={{ color: '#475569', fontSize: '11px', margin: '0 0 6px' }}>
                            Last enriched: {new Date(entry.last_web_enriched_at).toISOString().slice(0, 16).replace('T', ' ')} UTC
                            {' · '}Tier: {entry.enrichment_tier}
                        </p>

                        {enrichSources.length > 0 && (
                            <div>
                                <button onClick={() => setSourcesOpen(o => !o)} style={{
                                    background: 'none', border: 'none', color: '#475569', cursor: 'pointer',
                                    fontSize: '11px', padding: 0, marginBottom: '4px',
                                }}>
                                    {sourcesOpen ? '▲' : '▶'} Sources ({enrichSources.length})
                                </button>
                                {sourcesOpen && (
                                    <ol style={{ margin: '4px 0 0', paddingLeft: '18px', color: '#475569', fontSize: '11px' }}>
                                        {enrichSources.map((s, i) => <li key={i}>{s}</li>)}
                                    </ol>
                                )}
                            </div>
                        )}

                        {Object.keys(enrichConf).length > 0 && (
                            <div style={{ marginTop: '4px' }}>
                                <button onClick={() => setConfidenceOpen(o => !o)} style={{
                                    background: 'none', border: 'none', color: '#475569', cursor: 'pointer',
                                    fontSize: '11px', padding: 0, marginBottom: '4px',
                                }}>
                                    {confidenceOpen ? '▲' : '▶'} Confidence scores
                                </button>
                                {confidenceOpen && (
                                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {Object.entries(enrichConf).map(([k, v]) => (
                                            <span key={k} style={chip(
                                                v === 'high' ? '#22c55e' : v === 'medium' ? '#f59e0b' : v === 'low' ? '#ef4444' : '#6b7280'
                                            )}>
                                                {k}: {v}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

function PremiumEnrichModal({ entryId, onClose, onSuccess }) {
    const [estimate, setEstimate] = useState(null)
    const [loading, setLoading] = useState(true)
    const [enriching, setEnriching] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        api.get(`/lexis/entries/${entryId}/premium-enrich/estimate`)
            .then(r => { setEstimate(r.data); setLoading(false) })
            .catch(e => { setError(e.message); setLoading(false) })
    }, [entryId])

    const handleConfirm = async () => {
        setEnriching(true)
        try {
            await api.post(`/lexis/entries/${entryId}/premium-enrich`)
            onSuccess(estimate?.estimated_cost_usd)
        } catch (e) {
            setError(e.response?.data?.detail || e.message)
            setEnriching(false)
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
            <div style={{
                background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)',
                borderRadius: '12px', padding: '28px', maxWidth: '420px', width: '90%',
            }}>
                <h3 style={{ color: '#f1f5f9', margin: '0 0 16px', fontSize: '16px' }}>
                    ⭐ Premium Enrich — Cost Estimate
                </h3>
                {loading ? (
                    <p style={{ color: '#94a3b8' }}>Calculating estimate...</p>
                ) : error ? (
                    <p style={{ color: '#ef4444' }}>{error}</p>
                ) : (
                    <div>
                        <p style={{ color: '#e2e8f0', fontSize: '15px', margin: '0 0 8px' }}>
                            Estimated cost: <strong style={{ color: '#d4af37' }}>
                                ${estimate.estimated_cost_usd.toFixed(4)}
                            </strong>
                            {' · '}~{estimate.estimated_seconds}s
                        </p>
                        <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 20px' }}>
                            {estimate.breakdown.input_tokens} in / {estimate.breakdown.output_tokens} out
                            {' + '}{estimate.breakdown.tavily_searches} searches
                        </p>
                        <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 20px' }}>
                            Tier 3 uses Sonnet + Tavily web search for deeper product intelligence.
                            This will overwrite any existing Tier 2 data.
                        </p>
                    </div>
                )}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={onClose} disabled={enriching} style={{
                        padding: '8px 16px', background: 'rgba(148,163,184,0.1)',
                        border: '1px solid rgba(148,163,184,0.2)', borderRadius: '6px',
                        color: '#94a3b8', cursor: 'pointer', fontSize: '14px',
                    }}>
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading || enriching || !!error}
                        style={{
                            padding: '8px 16px',
                            background: (loading || enriching || error) ? '#374151' : '#7c3aed',
                            border: 'none', borderRadius: '6px',
                            color: 'white', cursor: (loading || enriching || error) ? 'not-allowed' : 'pointer',
                            fontSize: '14px', fontWeight: 600,
                        }}
                    >
                        {enriching ? '⏳ Enriching...' : 'Confirm and Enrich'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default function LexisEntry() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user, lang, setLang } = useAuth()
    const [entry, setEntry] = useState(null)
    const [clients, setClients] = useState([])
    const [files, setFiles] = useState([])
    const [tags, setTags] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('overview')
    const [deepEnriching, setDeepEnriching] = useState(false)
    const [publishing, setPublishing] = useState(false)
    const [showPremiumModal, setShowPremiumModal] = useState(false)
    const [toast, setToast] = useState(null)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3500)
    }

    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            const [entryRes, clientsRes, filesRes] = await Promise.all([
                api.get(`/lexis/entries/${id}`).then(r => r.data),
                api.get(`/lexis/entries/${id}/clients`).then(r => r.data).catch(() => []),
                api.get(`/lexis/entries/${id}/files`).then(r => r.data).catch(() => []),
            ])
            setEntry(entryRes)
            setClients(clientsRes)
            setFiles(filesRes)
            if (entryRes?.tags) setTags(toArray(entryRes.tags))
        } catch (e) {
            console.error('Fetch error:', e)
        }
        setLoading(false)
    }, [id])

    useEffect(() => { fetchData() }, [fetchData])

    const handleDeepEnrich = async () => {
        setDeepEnriching(true)
        try {
            await api.post(`/lexis/entries/${id}/deep-enrich`)
            await fetchData()
            showToast('Enriched (Tier 2)')
        } catch (e) {
            showToast(e.response?.data?.detail || e.message, 'error')
        }
        setDeepEnriching(false)
    }

    const handlePremiumSuccess = async (costUsd) => {
        setShowPremiumModal(false)
        await fetchData()
        showToast(`Enriched (Tier 3, $${(costUsd || 0).toFixed(4)} est.)`)
    }

    const handlePublishToWiki = async () => {
        setPublishing(true)
        try {
            const res = await api.post(`/lexis/entries/${id}/publish-to-wiki`)
            await fetchData()
            showToast(`Published to Wiki (${res.data.slug})`)
        } catch (e) {
            showToast(e.response?.data?.detail || e.message, 'error')
        }
        setPublishing(false)
    }

    if (loading) return <div style={{ padding: '40px', color: '#64748b' }}>Loading...</div>
    if (!entry) return <div style={{ padding: '40px', color: '#ef4444' }}>Entry not found</div>

    const overview = lang === 'vi' ? (entry.overview_vi || entry.overview_en) : entry.overview_en
    const overviewIsFallback = lang === 'vi' && !entry.overview_vi && !!entry.overview_en
    const capabilities = lang === 'vi' ? (entry.capabilities_vi || entry.capabilities_en) : entry.capabilities_en
    const capabilityList = capabilities.split('•').filter(s => s.trim())

    const categoryTags = tags.filter(t => t.startsWith('cat:')).map(t => t.replace('cat:', ''))
    const regularTags = tags.filter(t => !t.startsWith('cat:'))

    const statusColor = entry.status === 'published' ? '#22c55e' : '#f59e0b'
    const statusLabel = entry.status === 'published' ? 'Published' : 'Draft'

    const needsPublish = !(
        entry.status === 'published' &&
        entry.is_active === true &&
        entry.slug &&
        entry.published_at
    )
    const canPublish = user?.role === 'admin' || user?.role === 'editor'

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>

            {/* Toast notification */}
            {toast && (
                <div style={{
                    position: 'fixed', top: '20px', right: '20px', zIndex: 2000,
                    background: toast.type === 'error' ? '#7f1d1d' : '#14532d',
                    border: `1px solid ${toast.type === 'error' ? '#ef4444' : '#22c55e'}`,
                    color: toast.type === 'error' ? '#fca5a5' : '#86efac',
                    borderRadius: '8px', padding: '12px 18px', fontSize: '14px', fontWeight: 600,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                }}>
                    {toast.type === 'error' ? '✗' : '✓'} {toast.message}
                </div>
            )}

            {/* Premium Enrich modal */}
            {showPremiumModal && (
                <PremiumEnrichModal
                    entryId={id}
                    onClose={() => setShowPremiumModal(false)}
                    onSuccess={handlePremiumSuccess}
                />
            )}

            {/* 1. HEADER */}
            <div style={{ marginBottom: '24px' }}>
                <button onClick={() => navigate(-1)} style={{
                    background: 'none', border: 'none', color: '#94a3b8',
                    cursor: 'pointer', fontSize: '14px', marginBottom: '12px',
                }}>← Back</button>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, color: '#f1f5f9', flex: 1 }}>{entry.title}</h2>
                    <span style={{
                        background: `${statusColor}20`, color: statusColor,
                        padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: 600,
                    }}>{statusLabel}</span>
                    {entry.category_name && (
                        <span style={{
                            background: '#6366f120', color: '#6366f1',
                            padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: 600,
                        }}>{entry.category_name}</span>
                    )}
                    {categoryTags.map((cat, i) => (
                        <span key={i} style={{
                            background: 'rgba(168,85,247,0.15)', color: '#a855f7',
                            padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: 600,
                        }}>{cat}</span>
                    ))}
                </div>
                {user && ['admin', 'editor'].includes(user.role) && (
                    <button onClick={() => navigate(`/wiki/${entry.id}/edit`)} style={{
                        background: 'rgba(212,175,55,0.15)', color: '#d4af37',
                        border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px',
                        padding: '6px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 600,
                    }}>
                        📝 Edit in Wiki
                    </button>
                )}
                <p style={{ color: '#d4af37', margin: '8px 0 0', fontSize: '14px' }}>
                    🏢 {entry.vendor_name || 'Unknown vendor'} {entry.country_of_origin ? `(${entry.country_of_origin})` : ''}
                </p>
                {entry.last_reviewed_at && (
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '12px' }}>
                        Last reviewed: {new Date(entry.last_reviewed_at).toLocaleDateString()}
                    </p>
                )}
            </div>

            {/* Language toggle */}
            <div style={{ marginBottom: '16px' }}>
                <LangToggle value={lang} onChange={setLang} />
            </div>

            {/* Two-column layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px', alignItems: 'start' }}>
                {/* LEFT COLUMN (70%) */}
                <div>
                    {/* 2. OVERVIEW */}
                    <div style={{
                        background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                        border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
                    }}>
                        <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                            📝 {lang === 'vi' ? 'Tổng quan' : 'Overview'}
                        </h4>
                        {overviewIsFallback && (
                            <div style={{
                                background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
                                borderRadius: '6px', padding: '8px 12px', marginBottom: '12px',
                                fontSize: '12px', color: '#f59e0b',
                            }}>
                                ⚠️ Vietnamese version not available yet — showing English. Click <strong>Deep Enrich</strong> to generate Vietnamese content.
                            </div>
                        )}
                        <p style={{ color: '#e2e8f0', lineHeight: 1.7, fontSize: '14px', whiteSpace: 'pre-wrap', margin: 0 }}>
                            {overview || 'No overview available.'}
                        </p>
                    </div>

                    {/* 3. CAPABILITIES */}
                    {capabilityList.length > 0 && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                ⡡ {lang === 'vi' ? 'Khả năng' : 'Capabilities'}
                            </h4>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#e2e8f0', fontSize: '14px', lineHeight: 1.8 }}>
                                {capabilityList.map((cap, i) => (
                                    <li key={i} style={{ whiteSpace: 'pre-wrap' }}>{cap.trim()}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* 4. CLIENT MATCH PANEL */}
                    {clients.length > 0 && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
                                🎯 Client Match — Vietnamese LEA Agencies
                            </h4>
                            {clients.map((client, i) => (
                                <div key={i} style={{
                                    background: 'rgba(15,23,42,0.6)', borderRadius: '10px', padding: '16px',
                                    border: '1px solid rgba(148,163,184,0.1)', marginBottom: '12px',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div>
                                            <span style={{
                                                background: 'rgba(99,102,241,0.2)', color: '#6366f1',
                                                padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 700,
                                                marginRight: '8px',
                                            }}>{client.code_name}</span>
                                            <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}>
                                                {client.full_name_en || client.full_name_vi}
                                            </span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ color: '#22c55e', fontSize: '13px', fontWeight: 700 }}>
                                                {client.fit_score}/10
                                            </div>
                                            <div style={{
                                                width: '60px', height: '6px', background: 'rgba(148,163,184,0.2)',
                                                borderRadius: '3px', marginTop: '4px',
                                            }}>
                                                <div style={{
                                                    width: `${client.fit_score * 10}%`, height: '100%',
                                                    background: client.fit_score >= 8 ? '#22c55e' : client.fit_score >= 6 ? '#f59e0b' : '#ef4444',
                                                    borderRadius: '3px',
                                                }} />
                                            </div>
                                        </div>
                                    </div>
                                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: '8px 0 12px', lineHeight: 1.5 }}>
                                        {client.fit_reason}
                                    </p>
                                    {client.operation_types && client.operation_types.length > 0 && (
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>
                                                Operations:
                                            </span>
                                            {client.operation_types.map((op, j) => (
                                                <div key={j} style={{
                                                    marginLeft: '12px', marginTop: '6px',
                                                    borderLeft: '2px solid rgba(99,102,241,0.3)', paddingLeft: '10px',
                                                }}>
                                                    <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 600 }}>
                                                        • {op.operation_type}
                                                    </span>
                                                    <p style={{ color: '#94a3b8', fontSize: '12px', margin: '2px 0 0', lineHeight: 1.4 }}>
                                                        {op.use_case}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 5. OPERATION TYPES TABLE */}
                    {clients.some(c => c.operation_types?.length > 0) && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '20px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                📋 Operations Summary
                            </h4>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                                        <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Operation</th>
                                        <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Client</th>
                                        <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients.flatMap(c =>
                                        (c.operation_types || []).map(op => (
                                            <tr key={`${c.code_name}-${op.operation_type}`} style={{ borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
                                                <td style={{ padding: '8px', color: '#e2e8f0' }}>{op.operation_type}</td>
                                                <td style={{ padding: '8px' }}>
                                                    <span style={{
                                                        background: 'rgba(99,102,241,0.2)', color: '#6366f1',
                                                        padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
                                                    }}>{c.code_name}</span>
                                                </td>
                                                <td style={{ padding: '8px', color: '#94a3b8', fontSize: '12px' }}>{op.use_case}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* 6. INTERNAL NOTES (admin/editor only) */}
                    <RoleGate allowedRoles={['admin', 'editor']}>
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(239,68,68,0.2)', marginBottom: '20px',
                        }}>
                            <h4 style={{ color: '#ef4444', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                🔒 Internal Notes
                            </h4>
                            {entry.internal_notes ? (
                                <MarkdownView content={entry.internal_notes} />
                            ) : (
                                <p style={{ color: '#64748b', fontSize: '13px' }}>No internal notes.</p>
                            )}
                        </div>
                    </RoleGate>

                    {/* 7. INTELLIGENCE SECTION */}
                    <IntelligenceSection entry={entry} />
                </div>

                {/* RIGHT COLUMN (30%) — Sticky sidebar */}
                <div style={{ position: 'sticky', top: '20px' }}>
                    {/* 1. SOURCE FILES */}
                    <div style={{
                        background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                        border: '1px solid rgba(148,163,184,0.1)', marginBottom: '16px',
                    }}>
                        <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                            📁 Source Files ({files.length})
                        </h4>
                        {files.length === 0 ? (
                            <p style={{ color: '#64748b', fontSize: '13px' }}>No source documents attached</p>
                        ) : (
                            files.map((f, i) => {
                                const ext = (f.file_name || '').split('.').pop()?.toLowerCase() || 'file'
                                const isPdf = ext === 'pdf'
                                return (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        padding: '8px 0', borderBottom: i < files.length - 1 ? '1px solid rgba(148,163,184,0.1)' : 'none',
                                    }}>
                                        <span style={{ fontSize: '18px' }}>{FILE_IC[ext] || '📄'}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                color: '#e2e8f0', fontSize: '12px',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>{f.file_name}</div>
                                            <div style={{ color: '#64748b', fontSize: '11px' }}>{formatFileSize(f.file_size)}</div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                if (isPdf) {
                                                    window.open(f.file_url, '_blank')
                                                } else {
                                                    const a = document.createElement('a')
                                                    a.href = f.file_url
                                                    a.download = f.file_name
                                                    a.click()
                                                }
                                            }}
                                            style={{
                                                background: 'rgba(99,102,241,0.2)', color: '#6366f1',
                                                border: '1px solid rgba(99,102,241,0.3)', borderRadius: '4px',
                                                padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >{isPdf ? 'Open' : 'Download'}</button>
                                    </div>
                                )
                            })
                        )}
                    </div>

                    {/* 2. TAGS */}
                    {regularTags.length > 0 && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '16px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                🏷️ Tags
                            </h4>
                            <TagChips tags={regularTags} />
                        </div>
                    )}

                    {/* 3. RELATED ENTRIES */}
                    {entry.related_entry_ids && toArray(entry.related_entry_ids).length > 0 && (
                        <div style={{
                            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
                            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '16px',
                        }}>
                            <h4 style={{ color: '#d4af37', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
                                🔗 Related Entries
                            </h4>
                            {toArray(entry.related_entry_ids).map((relId, i) => (
                                <RelatedEntries key={i} entryId={relId} />
                            ))}
                        </div>
                    )}

                    {/* 4. VENDOR CARD */}
                    {entry.vendor_name && (
                        <div style={{ marginBottom: '16px' }}>
                            <VendorCard
                                vendor={{
                                    name: entry.vendor_name,
                                    country: entry.country_of_origin,
                                    is_represented: entry.is_represented,
                                }}
                            />
                        </div>
                    )}

                    {/* 5. ENRICH BUTTONS */}
                    <div style={{ marginTop: '16px', borderTop: '1px solid #334155', paddingTop: '16px' }}>
                        {/* Deep Enrich — all authenticated users */}
                        <button
                            onClick={handleDeepEnrich}
                            disabled={deepEnriching}
                            style={{
                                width: '100%', padding: '10px',
                                background: deepEnriching ? '#374151' : '#1e40af',
                                border: 'none', borderRadius: '6px', color: 'white',
                                cursor: deepEnriching ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px',
                                justifyContent: 'center', fontSize: '14px', fontWeight: 600,
                                marginBottom: '8px',
                            }}
                        >
                            {deepEnriching ? '🔄 Enriching...' : '🔍 Deep Enrich'}
                        </button>

                        {/* Premium Enrich — admin only */}
                        {user && user.role === 'admin' && (
                            <button
                                onClick={() => setShowPremiumModal(true)}
                                disabled={deepEnriching}
                                style={{
                                    width: '100%', padding: '10px',
                                    background: deepEnriching ? '#374151' : 'rgba(124,58,237,0.2)',
                                    border: '1px solid rgba(124,58,237,0.4)',
                                    borderRadius: '6px', color: '#a78bfa',
                                    cursor: deepEnriching ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    justifyContent: 'center', fontSize: '14px', fontWeight: 600,
                                }}
                            >
                                ⭐ Premium Enrich
                            </button>
                        )}

                        {entry.last_web_enriched_at && (
                            <p style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', textAlign: 'center' }}>
                                Last enriched: {new Date(entry.last_web_enriched_at).toLocaleString()}
                                {entry.enrichment_tier && ` (T${entry.enrichment_tier})`}
                            </p>
                        )}

                        {/* Publish to Wiki */}
                        <div style={{ marginTop: '8px', borderTop: '1px solid #334155', paddingTop: '8px' }}>
                            {!needsPublish ? (
                                <div
                                    title={`Slug: ${entry.slug}`}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        gap: '6px', padding: '8px', fontSize: '13px', color: '#22c55e',
                                        background: 'rgba(34,197,94,0.08)', borderRadius: '6px',
                                        border: '1px solid rgba(34,197,94,0.2)',
                                    }}
                                >
                                    ✅ In Wiki
                                </div>
                            ) : canPublish ? (
                                <button
                                    onClick={handlePublishToWiki}
                                    disabled={publishing}
                                    style={{
                                        width: '100%', padding: '10px',
                                        background: publishing ? '#374151' : 'rgba(34,197,94,0.15)',
                                        border: '1px solid rgba(34,197,94,0.4)',
                                        borderRadius: '6px', color: publishing ? '#94a3b8' : '#86efac',
                                        cursor: publishing ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        justifyContent: 'center', fontSize: '14px', fontWeight: 600,
                                    }}
                                >
                                    {publishing ? '🔄 Publishing...' : '📤 Publish to Wiki'}
                                </button>
                            ) : (
                                <div style={{ padding: '8px', textAlign: 'center', fontSize: '12px', color: '#64748b' }}>
                                    Draft (editor required to publish)
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
